import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './ServerManagement.css';

const ServerManagement = () => {
    return (
        <>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>Managed Server Administration - AWS, Azure, GCP & More | Epicfinal</title>
                <meta name="title" content="Managed Server Administration - AWS, Azure, GCP & More | Epicfinal" />
                <meta name="description" content="Full server management at $139.90/mo. 24/7 monitoring, security updates, performance tuning & ongoing maintenance. Works with AWS, Azure, Google Cloud or any hosting provider." />
                <meta name="keywords" content="server management, managed server, cloud server management, AWS management, Azure management, Google Cloud management, server monitoring, server security, server maintenance, external server management, VPS management, dedicated server management" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="author" content="Epicfinal" />
                <link rel="canonical" href="https://epicfinal.com/server-management" />

                {/* Language & Locale */}
                <html lang="en" />
                <meta property="og:locale" content="en_US" />
                <link rel="alternate" href="https://epicfinal.com/server-management" hrefLang="en" />
                <link rel="alternate" href="https://epicfinal.com/server-management" hrefLang="x-default" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://epicfinal.com/server-management" />
                <meta property="og:title" content="Managed Server Administration | Epicfinal" />
                <meta property="og:description" content="Full server management at $139.90/mo. 24/7 monitoring, security updates & maintenance for any cloud platform." />
                <meta property="og:image" content="https://epicfinal.com/android-chrome-512x512.png" />
                <meta property="og:image:alt" content="Epicfinal Server Management - Multi-Cloud Support" />
                <meta property="og:site_name" content="Epicfinal" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://epicfinal.com/server-management" />
                <meta name="twitter:title" content="Managed Server Administration | Epicfinal" />
                <meta name="twitter:description" content="Full server management at $139.90/mo. Works with AWS, Azure, GCP or any provider." />
                <meta name="twitter:image" content="https://epicfinal.com/android-chrome-512x512.png" />

                {/* Schema.org Structured Data */}
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        "name": "Public Cloud & External Server Management",
                        "description": "Full server management including monitoring, security updates, performance tuning and ongoing maintenance for any cloud or hosting provider",
                        "provider": {"@type": "Organization", "name": "Epicfinal", "url": "https://epicfinal.com"},
                        "serviceType": "Server Management",
                        "areaServed": "Worldwide",
                        "offers": {"@type": "Offer", "price": "139.90", "priceCurrency": "USD", "priceValidUntil": "2025-12-31", "availability": "https://schema.org/InStock"},
                        "url": "https://epicfinal.com/server-management"
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Managed Server Administration",
                        "url": "https://epicfinal.com/server-management",
                        "breadcrumb": {
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                                {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://epicfinal.com"},
                                {"@type": "ListItem", "position": 2, "name": "Managed Services", "item": "https://epicfinal.com/managed-services"},
                                {"@type": "ListItem", "position": 3, "name": "Server Management", "item": "https://epicfinal.com/server-management"}
                            ]
                        }
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {"@type": "Question", "name": "What cloud providers do you support?", "acceptedAnswer": {"@type": "Answer", "text": "We support all major cloud providers including AWS, Microsoft Azure, Google Cloud Platform (GCP), DigitalOcean, Linode, and any other hosting provider worldwide."}},
                            {"@type": "Question", "name": "What does server management include?", "acceptedAnswer": {"@type": "Answer", "text": "Our service includes 24/7 monitoring, security updates, performance optimization, ongoing maintenance, vulnerability scanning, and expert technical support."}},
                            {"@type": "Question", "name": "How many servers does $139.90 cover?", "acceptedAnswer": {"@type": "Answer", "text": "The $139.90/month pricing covers management of one server, whether virtual or dedicated. Contact us for multi-server pricing."}}
                        ]
                    }
                `}</script>
            </Helmet>

            <div className="server-mgmt-page">
                {/* Hero Section */}
                <section className="server-hero">
                    <div className="container">
                        <div className="server-hero-content">
                            <div className="server-icon-badge">
                                <i className="las la-server"></i>
                            </div>
                            <h1 className="server-hero-title">
                                Public Cloud / External Server Management
                            </h1>
                            <p className="server-hero-subtitle">
                                Full management for your cloud or external server
                            </p>
                            <p className="server-hero-description">
                                We provide full management for a single server hosted on a public cloud platform (such as AWS) or with any other hosting provider – including monitoring, security updates, performance tuning and ongoing maintenance, so your environment stays stable and reliable.
                            </p>
                            <div className="server-hero-buttons">
                                <a href="https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-external-server" 
                                   className="btn-server btn-primary" target="_blank" rel="noopener noreferrer">
                                    Order Now - $139.90/mo
                                </a>
                                <Link to="/contact1" className="btn-server btn-secondary">
                                    Talk to Expert
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="server-features">
                    <div className="container">
                        <div className="server-section-header">
                            <h2>Complete Server Management</h2>
                            <p>Everything you need for a stable, secure server</p>
                        </div>
                        <div className="server-features-grid">
                            <div className="server-feature-card">
                                <div className="server-feature-icon">
                                    <i className="las la-chart-line"></i>
                                </div>
                                <h3>Continuous Monitoring</h3>
                                <p>monitoring with smart agent tracking CPU, memory, disk, and network performance.</p>
                            </div>
                            <div className="server-feature-card">
                                <div className="server-feature-icon">
                                    <i className="las la-shield-alt"></i>
                                </div>
                                <h3>Security Updates</h3>
                                <p>Regular security patches and updates to keep your server protected from threats.</p>
                            </div>
                            <div className="server-feature-card">
                                <div className="server-feature-icon">
                                    <i className="las la-cogs"></i>
                                </div>
                                <h3>Performance Tuning</h3>
                                <p>Ongoing optimization to ensure peak server performance and resource efficiency.</p>
                            </div>
                            <div className="server-feature-card">
                                <div className="server-feature-icon">
                                    <i className="las la-tools"></i>
                                </div>
                                <h3>Ongoing Maintenance</h3>
                                <p>Regular maintenance tasks, updates, and proactive issue resolution.</p>
                            </div>
                            <div className="server-feature-card">
                                <div className="server-feature-icon">
                                    <i className="las la-headset"></i>
                                </div>
                                <h3>Expert Support</h3>
                                <p>Direct access to our team of experienced server administrators.</p>
                            </div>
                            <div className="server-feature-card">
                                <div className="server-feature-icon">
                                    <i className="las la-cloud"></i>
                                </div>
                                <h3>Multi-Cloud Support</h3>
                                <p>Works with AWS, Azure, Google Cloud, or any hosting provider.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* What's Included Section */}
                <section className="server-included">
                    <div className="container">
                        <div className="server-section-header">
                            <h2>What's Included</h2>
                            <p>Comprehensive server management services</p>
                        </div>
                        <div className="server-included-grid">
                            <div className="server-included-column">
                                <h3><i className="las la-check-circle"></i> Monitoring & Alerts</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> Smart monitoring agent installation</li>
                                    <li><i className="las la-angle-right"></i> Real-time resource monitoring</li>
                                    <li><i className="las la-angle-right"></i> Instant alerts for issues</li>
                                    <li><i className="las la-angle-right"></i> Performance metrics tracking</li>
                                </ul>
                            </div>
                            <div className="server-included-column">
                                <h3><i className="las la-check-circle"></i> Security & Updates</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> Integrated security component</li>
                                    <li><i className="las la-angle-right"></i> Regular system updates</li>
                                    <li><i className="las la-angle-right"></i> Security patches</li>
                                    <li><i className="las la-angle-right"></i> Vulnerability scanning</li>
                                </ul>
                            </div>
                            <div className="server-included-column">
                                <h3><i className="las la-check-circle"></i> Maintenance & Support</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> Ongoing maintenance</li>
                                    <li><i className="las la-angle-right"></i> Performance optimization</li>
                                    <li><i className="las la-angle-right"></i> Issue troubleshooting</li>
                                    <li><i className="las la-angle-right"></i> Expert technical support</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Pricing Section */}
                <section className="server-pricing">
                    <div className="container">
                        <div className="server-pricing-card">
                            <div className="server-pricing-header">
                                <h2>Server Management Service</h2>
                                <p>Complete management for one server</p>
                            </div>
                            <div className="server-pricing-body">
                                <div className="server-pricing-amount">
                                    <span className="server-price">$139.90</span>
                                    <span className="server-period">USD / Monthly</span>
                                </div>
                                <ul className="server-pricing-features">
                                    <li><i className="las la-check"></i> One server (virtual or dedicated)</li>
                                    <li><i className="las la-check"></i> Smart monitoring agent</li>
                                    <li><i className="las la-check"></i> 24/7 continuous monitoring</li>
                                    <li><i className="las la-check"></i> Regular system updates</li>
                                    <li><i className="las la-check"></i> Security patches & hardening</li>
                                    <li><i className="las la-check"></i> Performance optimization</li>
                                    <li><i className="las la-check"></i> Ongoing maintenance</li>
                                    <li><i className="las la-check"></i> Expert technical support</li>
                                </ul>
                                <div className="server-pricing-note">
                                    <i className="las la-info-circle"></i>
                                    <p><strong>Note:</strong> Service covers one server only. Works with any cloud provider or hosting platform.</p>
                                </div>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-external-server" 
                                   className="server-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started Now
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="server-cta">
                    <div className="container">
                        <div className="server-cta-content">
                            <h2>Ready to Simplify Server Management?</h2>
                            <p>Let our experts handle your server while you focus on your business</p>
                            <div className="server-cta-buttons">
                                <a href="https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-external-server" 
                                   className="btn-server btn-light" target="_blank" rel="noopener noreferrer">
                                    Order Service
                                </a>
                                <a href="https://meetings.hubspot.com/viacheslav-seifer" 
                                   className="btn-server btn-outline" target="_blank" rel="noopener noreferrer">
                                    Schedule Consultation
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default ServerManagement;

